import React, {useCallback, useMemo, useState} from 'react';
import {View, Text, TouchableOpacity, ScrollView, Alert} from 'react-native';
import Accordion from 'react-native-collapsible/Accordion';
import {useFocusEffect} from '@react-navigation/native';

import {text} from '../text';
import {theme} from '../constants';
import {components} from '../components';
import {useAppNavigation, useAppSelector} from '../hooks';
import {homeIndicatorHeight as getHomeIndicatorHeight} from '../utils';

type StoredOrderEntry = {
  response?: Record<string, unknown> | null;
  payload?: Record<string, unknown> | null;
  totals?: Record<string, unknown> | null;
  createdAt?: string | null;
};

type NormalizedOrderProduct = {
  id: string | number;
  name: string;
  quantity: number;
  price: number;
  image?: unknown;
  filling?: unknown;
};

type NormalizedOrderHistoryItem = {
  id: string;
  orderId: string | number | null;
  date: string;
  time: string;
  total: number;
  status: string;
  statusKey: string;
  delivery: number;
  discount: number;
  products: NormalizedOrderProduct[];
  raw: StoredOrderEntry;
};

const OrderHistory: React.FC = (): JSX.Element => {
  const navigation = useAppNavigation();
  const user = useAppSelector((state) => state.userSlice.user);
  const [activeSections, setActiveSections] = useState<number[]>([]);
  const [history, setHistory] = useState<NormalizedOrderHistoryItem[]>([]);

  const homeIndicatorHeight = getHomeIndicatorHeight();

  const isCarrefourUser = useMemo(() => {
    if (!user) {
      return false;
    }

    return Object.values(user as Record<string, unknown>).some((value) => {
      if (typeof value !== 'string') {
        return false;
      }

      return value.trim().toLowerCase() === 'carrefour';
    });
  }, [user]);

  const parseNumeric = useCallback((value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = parseFloat(value);

      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }

    return null;
  }, []);

  const normalizeSessionOrder = useCallback(
    (entry: any, index: number): NormalizedOrderHistoryItem | null => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }

      const safeEntry = entry as StoredOrderEntry;
      const totals =
        safeEntry.totals && typeof safeEntry.totals === 'object'
          ? (safeEntry.totals as Record<string, unknown>)
          : {};
      const payload =
        safeEntry.payload && typeof safeEntry.payload === 'object'
          ? (safeEntry.payload as Record<string, unknown>)
          : {};
      const response =
        safeEntry.response && typeof safeEntry.response === 'object'
          ? (safeEntry.response as Record<string, unknown>)
          : {};

      const orderIdentifier =
        response.order_id ??
        response.orderId ??
        response.id ??
        payload.orderId ??
        payload.order_id ??
        `order-${index + 1}`;

      const createdAtCandidate =
        safeEntry.createdAt ??
        (typeof response.created_at === 'string' ? response.created_at : null) ??
        (typeof response.createdAt === 'string' ? response.createdAt : null) ??
        null;

      let createdDate: Date | null = null;
      if (typeof createdAtCandidate === 'string') {
        const parsedDate = new Date(createdAtCandidate);
        if (!Number.isNaN(parsedDate.getTime())) {
          createdDate = parsedDate;
        }
      }

      if (!createdDate) {
        createdDate = new Date();
      }

      let dateLabel = 'Unknown date';
      let timeLabel = '';

      try {
        dateLabel = createdDate.toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
        timeLabel = `at ${createdDate.toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
        })}`;
      } catch (error) {
        dateLabel = createdDate.toDateString();
        timeLabel = `at ${createdDate.toTimeString().slice(0, 5)}`;
      }

      const items = Array.isArray((payload as any).items)
        ? ((payload as any).items as Array<Record<string, unknown>>)
        : [];

      const products: NormalizedOrderProduct[] = items.map((item, productIndex) => {
        const rawQuantity = parseNumeric((item as any).quantity) ?? 1;
        const quantity = Math.max(1, Math.round(rawQuantity));
        const price = parseNumeric((item as any).price) ?? 0;

        const product: NormalizedOrderProduct = {
          id:
            typeof (item as any).id === 'string' || typeof (item as any).id === 'number'
              ? ((item as any).id as string | number)
              : `${index}-${productIndex}`,
          name:
            typeof (item as any).name === 'string' && (item as any).name?.trim()
              ? ((item as any).name as string)
              : 'Unknown item',
          quantity,
          price,
        };

        if ((item as any).image !== undefined) {
          product.image = (item as any).image;
        }

        if ((item as any).filling !== undefined) {
          product.filling = (item as any).filling;
        }

        return product;
      });

      const totalsTotal =
        parseNumeric((totals as any).total) ??
        parseNumeric((totals as any).totalPrice) ??
        parseNumeric((totals as any).total_price) ??
        parseNumeric((response as any).total) ??
        parseNumeric((response as any).totalPrice) ??
        parseNumeric((response as any).total_price);

      const itemsTotal = products.reduce(
        (sum, product) => sum + product.quantity * product.price,
        0,
      );

      const total = totalsTotal ?? itemsTotal;
      const delivery =
        parseNumeric((totals as any).delivery) ?? parseNumeric((response as any).delivery) ?? 0;
      const discount =
        parseNumeric((totals as any).discount) ?? parseNumeric((response as any).discount) ?? 0;

      const statusCandidate =
        typeof response.status === 'string'
          ? response.status
          : typeof (payload as any).status === 'string'
          ? (payload as any).status
          : 'Processing';
      const statusString =
        typeof statusCandidate === 'string' && statusCandidate.trim()
          ? statusCandidate.trim()
          : 'Processing';
      const statusLower = statusString.toLowerCase();
      const statusDisplay = statusLower
        .split(' ')
        .filter((part) => part.trim().length > 0)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ') || 'Processing';

      return {
        id:
          typeof orderIdentifier === 'string' || typeof orderIdentifier === 'number'
            ? String(orderIdentifier)
            : `session-${index}`,
        orderId:
          typeof orderIdentifier === 'string' || typeof orderIdentifier === 'number'
            ? orderIdentifier
            : null,
        date: dateLabel,
        time: timeLabel,
        total,
        status: statusDisplay,
        statusKey: statusLower,
        delivery,
        discount,
        products,
        raw: safeEntry,
      };
    },
    [parseNumeric],
  );

  useFocusEffect(
    useCallback(() => {
      const globalScope = globalThis as {
        sessionStorage?: {
          getItem: (key: string) => string | null;
        };
        __LEVELDELIVERY_ORDERS__?: unknown;
      };

      let ordersSource: unknown = [];

      try {
        if (globalScope.sessionStorage) {
          const stored = globalScope.sessionStorage.getItem('leveldelivery.orders');
          ordersSource = stored ? JSON.parse(stored) : [];
        } else if (Array.isArray(globalScope.__LEVELDELIVERY_ORDERS__)) {
          ordersSource = globalScope.__LEVELDELIVERY_ORDERS__;
        }
      } catch (error) {
        console.warn('Failed to read order history from session storage.', error);
        ordersSource = [];
      }

      const normalized = Array.isArray(ordersSource)
        ? ordersSource
            .map((entry, index) => normalizeSessionOrder(entry, index))
            .filter(
              (item): item is NormalizedOrderHistoryItem => item !== null,
            )
        : [];

      setHistory(normalized);
    }, [normalizeSessionOrder]),
  );

  const formatAmount = useCallback(
    (value: unknown): string => {
      const parsed = parseNumeric(value);
      const amount = parsed !== null ? parsed : 0;
      return amount.toFixed(2);
    },
    [parseNumeric],
  );

  const shouldShowCarrefourTicket = (order: NormalizedOrderHistoryItem): boolean => {
    if (!order) {
      return false;
    }

    const status = order.statusKey;

    if (status !== 'gone') {
      return false;
    }

    if (!isCarrefourUser) {
      return false;
    }

    return order.total > 0;
  };

  const handleCarrefourTicketPress = (order: NormalizedOrderHistoryItem) => {
    const rawResponse = order.raw?.response as
      | Record<string, unknown>
      | undefined
      | null;
    const responseId = rawResponse?.id;
    const fallbackId =
      typeof responseId === 'string' || typeof responseId === 'number'
        ? responseId
        : null;
    const orderId = order?.orderId ?? fallbackId;
    const orderReference = orderId ? ` ${orderId}` : '';

    Alert.alert(
      'Ticket to Carrefour',
      `Support ticket created for order${orderReference}.`,
    );
  };

  const setSections = (sections: any) => {
    setActiveSections(sections.includes(undefined) ? [] : sections);
  };

  const renderStatusBar = () => {
    return <components.StatusBar />;
  };

  const renderHeader = () => {
    return <components.Header goBack={true} title='Order history' />;
  };

  const accordionHeader = (section: NormalizedOrderHistoryItem) => {
    return (
      <View
        style={{
          marginHorizontal: 20,
          backgroundColor: theme.colors.backgroundPrimary,
          paddingHorizontal: 20,
          paddingTop: 20,
          paddingBottom: 17,
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 7,
          }}
        >
          <View style={{flexDirection: 'row', alignItems: 'flex-end'}}>
            <Text
              style={{
                ...theme.fonts.DMSans_500Medium,
                fontSize: 14,
                lineHeight: 14 * 1.2,
                color: theme.colors.textPrimary,
                marginRight: 4,
              }}
            >
              {section.date}
            </Text>
            <Text
              style={{
                ...theme.fonts.DMSans_400Regular,
                fontSize: 10,
                lineHeight: 10 * 1.2,
                marginBottom: 2,
                color: theme.colors.textMuted,
              }}
            >
              {section.time}
            </Text>
          </View>
          <Text
            style={{
              ...theme.fonts.DMSans_500Medium,
              fontSize: 14,
              lineHeight: 14 * 1.2,
              color: theme.colors.textPrimary,
            }}
          >
            ${formatAmount(section.total)}
          </Text>
        </View>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Text
            style={{
              ...theme.fonts.DMSans_400Regular,
              color: theme.colors.textMuted,
              fontSize: 12,
              lineHeight: 12 * 1.5,
            }}
          >
            Order ID: {section.orderId ?? 'N/A'}
          </Text>

          <View
            style={{
              paddingHorizontal: 8,
              paddingVertical: 3,
              borderRadius: 5,
              backgroundColor:
                section.statusKey === 'shipping'
                  ? '#FFA462'
                  : section.statusKey === 'delivered'
                  ? theme.colors.accentPrimary
                  : '#FA5555',
            }}
          >
            <Text
              style={{
                ...theme.fonts.DMSans_500Medium,
                fontSize: 10,
                lineHeight: 10 * 1.5,
                color: theme.colors.backgroundPrimary,
              }}
            >
              {section.status}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const accordionContent = (section: NormalizedOrderHistoryItem) => {
    return (
      <View
        style={{
          marginHorizontal: 20,
          borderTopWidth: 1,
          borderTopColor: '#DBE9F5',
        }}
      >
        <View
          style={{
            backgroundColor: theme.colors.backgroundPrimary,
            padding: 20,
            borderBottomLeftRadius: 10,
            borderBottomRightRadius: 10,
            marginBottom: 10,
          }}
        >
          {section.products.map((item) => {
            return (
              <View
                key={item.id}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 10,
                }}
              >
                <text.T14>{item.name}</text.T14>
                <text.T14>
                  {item.quantity} x ${formatAmount(item.price)}
                </text.T14>
              </View>
            );
          })}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 10,
            }}
          >
            <text.T14>Discount</text.T14>
            <text.T14>- ${formatAmount(section.discount)}</text.T14>
          </View>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <text.T14>Delivery</text.T14>
            <text.T14>${formatAmount(section.delivery)}</text.T14>
          </View>
        </View>
        {section.statusKey === 'shipping' && (
          <components.Button
            title='track order'
            onPress={() => {
              navigation.navigate('TrackYourOrder');
            }}
          />
        )}
        {section.statusKey === 'delivered' && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <components.Button
              title='repeat order'
              transparent={true}
              containerStyle={{
                width: '48%',
              }}
            />
            <components.Button
              title='Leave review'
              containerStyle={{
                width: '48%',
              }}
            />
          </View>
        )}
        {shouldShowCarrefourTicket(section) && (
          <components.Button
            title='Ticket to Carrefour'
            containerStyle={{marginTop: 10}}
            onPress={() => handleCarrefourTicketPress(section)}
          />
        )}
      </View>
    );
  };

  const renderContent = () => {
    if (history.length > 0) {
      return (
        <ScrollView contentContainerStyle={{paddingBottom: 20, flexGrow: 1}}>
          <Accordion
            activeSections={activeSections}
            sections={history}
            touchableComponent={TouchableOpacity}
            renderHeader={accordionHeader}
            renderContent={accordionContent}
            duration={400}
            onChange={setSections}
            containerStyle={{paddingTop: 10}}
            sectionContainerStyle={{marginBottom: 10}}
          />
        </ScrollView>
      );
    }
  };

  const renderEmptyHistory = () => {
    if (history.length === 0) {
      return (
        <ScrollView
          contentContainerStyle={{flexGrow: 1, paddingTop: 10}}
          showsVerticalScrollIndicator={false}
        >
          <View
            style={{
              backgroundColor: theme.colors.backgroundPrimary,
              flex: 1,
              marginHorizontal: 20,
              paddingHorizontal: 20,
              borderRadius: 10,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <components.Image
              source={{uri: 'https://george-fx.github.io/dine-hub/13.jpg'}}
              style={{
                width: theme.sizes.width - 100,
                aspectRatio: 1,
                alignSelf: 'center',
                marginBottom: 14,
              }}
            />
            <text.H2
              style={{
                marginBottom: 14,
              }}
            >
              No Order History Yet!
            </text.H2>
            <text.T16 style={{textAlign: 'center'}}>
              It looks like your order history is empty.{'\n'}Place your order
              now to start building{'\n'}your history!
            </text.T16>
          </View>
        </ScrollView>
      );
    }

    return null;
  };

  const renderButton = () => {
    if (history.length === 0) {
      return (
        <View
          style={{
            paddingHorizontal: 20,
            paddingTop: 20,
            marginBottom: homeIndicatorHeight === 0 ? 20 : 10,
          }}
        >
          <components.Button
            title='Explore Our Menu'
            onPress={() => {
              navigation.navigate('VerifyYourPhoneNumber');
            }}
          />
        </View>
      );
    }

    return null;
  };

  const renderHomeIndicator = () => {
    return <components.HomeIndicator />;
  };

  return (
    <components.SmartView>
      {renderStatusBar()}
      {renderHeader()}
      {renderContent()}
      {renderEmptyHistory()}
      {renderButton()}
      {renderHomeIndicator()}
    </components.SmartView>
  );
};

export default OrderHistory;
